"use client";

import { useState } from "react";
import type React from "react";
import {
  PuzzleIcon,
  UserIcon,
  HelpCircleIcon,
  UsersIcon,
  FileTextIcon,
  HomeIcon,
  SettingsIcon,
} from "lucide-react";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

import { HelpCenterSheet } from "./help-center-sheet";
import { Separator } from "@/components/ui/separator";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

export function NavSecondary({
  onNavigate,
  activePage,
  ...props
}: {
  onNavigate: (page: string) => void;
  activePage: string;
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);

  // Check feature flag for team management
  const teamManagementFlag = useFeatureFlag("feature_team_management");
  const integrationsFlag = useFeatureFlag("feature_integrations");
  const formsFlag = useFeatureFlag("feature_forms");
  const dashboardFlag = useFeatureFlag("feature_dashboard");
  const settingsFlag = useFeatureFlag("feature_settings");

  // Check if flags are still loading
  const loading =
    teamManagementFlag.loading ||
    integrationsFlag.loading ||
    formsFlag.loading ||
    dashboardFlag.loading ||
    settingsFlag.loading;

  // Create a new array with the exact items we want
  const customItems = [
    {
      title: "Dashboard",
      id: "dashboard",
      url: "#",
      icon: HomeIcon,
      onClick: () => onNavigate("dashboard"),
      enabled: dashboardFlag.enabled,
    },
    {
      title: "Integrations",
      id: "integrations",
      url: "#",
      icon: PuzzleIcon,
      onClick: () => onNavigate("integrations"),
      enabled: integrationsFlag.enabled,
    },
    {
      title: "Forms",
      id: "forms",
      url: "#",
      icon: FileTextIcon,
      onClick: () => onNavigate("forms"),
      enabled: formsFlag.enabled,
    },
    {
      title: "Team",
      id: "team",
      url: "#",
      icon: UsersIcon,
      onClick: () => onNavigate("team"),
      enabled: teamManagementFlag.enabled,
    },
    // {
    //   title: "Settings",
    //   id: "settings",
    //   url: "#",
    //   icon: SettingsIcon,
    //   onClick: () => onNavigate("settings"),
    //   enabled: true, // Enable by default - newly migrated feature
    // },
    {
      title: "Account",
      id: "profile",
      url: "#",
      icon: UserIcon,
      onClick: () => onNavigate("profile"),
      enabled: true,
    },
    {
      title: "Help Center",
      id: "help-center",
      url: "#",
      icon: HelpCircleIcon,
      onClick: () => setHelpCenterOpen(true),
      enabled: true, // Always show help center
    },
  ].filter((item) => item.enabled); // Only show enabled features

  // Show loading state while feature flags are being resolved
  if (loading) {
    return (
      <SidebarGroup className="mt-0" {...props}>
        <Separator className="mx-2 my-1 bg-sidebar-border/50" />
        <SidebarGroupContent>
          <SidebarMenu>
            <div className="text-sm text-muted-foreground px-2 py-1">
              Loading...
            </div>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <>
      <SidebarGroup className="mt-0" {...props}>
        <Separator className="mx-2 my-1 bg-sidebar-border/50" />
        <SidebarGroupContent>
          <SidebarMenu>
            {customItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={activePage === item.id}
                  onClick={item.onClick}
                >
                  <a href={item.url} onClick={(e) => e.preventDefault()}>
                    <item.icon />
                    <span>{item.title}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <HelpCenterSheet open={helpCenterOpen} onOpenChange={setHelpCenterOpen} />
    </>
  );
}
